import { createFileRoute, Link, Outlet, useMatches } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import {
  Badge,
  EmptyState,
  Input,
  Pagination,
  Spinner,
  Table,
  Td,
  Th,
} from '@/components';
import { formatAdminDateTime } from '@/lib/format';
import { apiJson } from '@/lib/api';

type ThreadStatus = 'open' | 'archived';

type ThreadListItem = {
  id: string;
  status: ThreadStatus;
  created_at: string;
  updated_at: string;
  last_message_at: string | null;
  message_preview: string | null;
  unread: boolean;
  installation_id: string | null;
  account_id: string | null;
  user_email: string | null;
  user_display_name: string | null;
};

type ListRes = {
  items: ThreadListItem[];
  total: number;
  page: number;
  page_size: number;
};

function statusBadge(status: ThreadStatus, unread: boolean) {
  if (unread) return <Badge variant="primary">Unread</Badge>;
  if (status === 'archived') return <Badge variant="neutral">Archived</Badge>;
  return <Badge variant="neutral">Open</Badge>;
}

function SupportList() {
  const [q, setQ] = useState('');
  const [qDebounced, setQDebounced] = useState('');
  const [status, setStatus] = useState<ThreadStatus | ''>('');
  const [page, setPage] = useState(1);
  const pageSize = 50;

  useEffect(() => {
    const t = setTimeout(() => {
      setQDebounced(q);
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ['admin', 'support', qDebounced, status, page, pageSize],
    queryFn: () => {
      const params = new URLSearchParams();
      if (qDebounced.trim()) params.set('q', qDebounced.trim());
      if (status) params.set('status', status);
      params.set('page', String(page));
      params.set('page_size', String(pageSize));
      params.set('sort', 'last_message_at_desc');
      return apiJson<ListRes>(`/v1/admin/support/threads?${params.toString()}`);
    },
  });

  return (
    <div className="space-y-4">
      <h1 className="text-2xl font-semibold">Support</h1>
      <p className="text-sm text-gray-500">
        Ongoing chat with users. Replies notify them in the app via push.
      </p>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="min-w-0 flex-1">
          <Input
            label="Search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Message, email, or ID"
          />
        </div>
        <div>
          <label className="mb-2 block text-sm text-gray-500">Status</label>
          <select
            className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm"
            value={status}
            onChange={(e) => {
              setStatus(e.target.value as ThreadStatus | '');
              setPage(1);
            }}
          >
            <option value="">All</option>
            <option value="open">Open</option>
            <option value="archived">Archived</option>
          </select>
        </div>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      )}
      {isError && (
        <p className="text-sm text-red-600">
          {(error as Error).message}
          <button type="button" className="ml-2 underline" onClick={() => void refetch()}>
            Retry
          </button>
        </p>
      )}
      {data && data.items.length === 0 && (
        <EmptyState title="No support threads yet" hint="Messages from the app will appear here." />
      )}
      {data && data.items.length > 0 && (
        <>
          <div className="max-w-full overflow-x-auto">
            <Table>
              <thead>
                <tr>
                  <Th>Last activity</Th>
                  <Th>Status</Th>
                  <Th>Preview</Th>
                  <Th>User</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((item) => (
                  <tr key={item.id}>
                    <Td>
                      <Link
                        to="/admin/support/$id"
                        params={{ id: item.id }}
                        className="font-medium text-primary hover:underline"
                      >
                        {item.last_message_at
                          ? formatAdminDateTime(item.last_message_at)
                          : formatAdminDateTime(item.created_at)}
                      </Link>
                    </Td>
                    <Td>{statusBadge(item.status, item.unread)}</Td>
                    <Td className="max-w-md truncate">
                      <Link
                        to="/admin/support/$id"
                        params={{ id: item.id }}
                        className="hover:underline"
                      >
                        {item.message_preview ?? '—'}
                      </Link>
                    </Td>
                    <Td>
                      {item.user_display_name ?? item.user_email ?? (
                        <span className="font-mono text-xs text-gray-500">
                          {item.installation_id?.slice(0, 8) ?? '—'}
                        </span>
                      )}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
          <Pagination page={page} pageSize={pageSize} total={data.total} onChange={setPage} />
        </>
      )}
    </div>
  );
}

function SupportLayout() {
  const matches = useMatches();
  const leafId = matches.at(-1)?.routeId;
  if (leafId === '/admin/support/$id') {
    return <Outlet />;
  }
  return <SupportList />;
}

export const Route = createFileRoute('/admin/support')({
  component: SupportLayout,
});
